/**
 * URL parameters. Players only ever see `?room=CODE` (invite links). The rest are dev
 * switches used for screenshots and quick testing, e.g.
 *   ?offline=1&seed=3&t=600&speed=20&bots=5     straight into an offline match at 10:00
 *   ?offline=1&t=300&until=inbound              …and on until a missile is inbound
 *   ?screen=menu|lobby|results|auth             a screen with placeholder data
 *   &zoom=20&look=2.3,48.8                      camera zoom (css px per mu) and centre (lon,lat or "focus")
 */
import type { BotDifficulty, RegionId } from '@sa/sim';
import type { UntilEvent } from './offline/protocol';

const q = new URLSearchParams(location.search);
const num = (k: string, d: number) => {
  const v = Number(q.get(k));
  return q.has(k) && Number.isFinite(v) ? v : d;
};

export const params = {
  room: q.get('room')?.toUpperCase() ?? null,
  offline: q.get('offline') === '1',
  seed: num('seed', 0),
  /** seconds of match time to simulate before the first frame */
  skipSeconds: num('t', 0),
  speed: Math.max(0.25, Math.min(50, num('speed', 1))),
  bots: Math.max(1, Math.min(7, num('bots', 5))),
  difficulty: (q.get('diff') ?? 'normal') as BotDifficulty,
  region: q.has('region') ? (num('region', 0) as RegionId) : null,
  until: (q.get('until') as UntilEvent | null) ?? undefined,
  autopilot: q.get('auto') === '1',
  zoom: q.has('zoom') ? num('zoom', 9) : null,
  /** camera centre as "lon,lat", or "focus" (our push front) */
  look: q.get('look') === 'focus' ? ('focus' as const) : (q.get('look')?.split(',').map(Number) as [number, number] | undefined),
  screen: q.get('screen'),
  /** `screen=lobby`: show a teams lobby with a fixed team layout */
  teams: q.get('teams') === '1',
  /** offline dev: knock our seat out (capital captured) at this match second (≥ 600, after capitals are exposed) */
  devElim: q.has('elim') ? num('elim', 0) : null,
  /** offline dev: at this match second (≥ 600) our capital goes critical (§1.11), as after a lethal blow */
  devCrit: q.has('crit') ? num('crit', 0) : null,
  /** offline dev: …and the capital of the rival nearest to ours */
  devRivalCrit: q.has('rcrit') ? num('rcrit', 0) : null,
  /** offline dev: at this match second the nearest hostile silos fire a two-missile salvo at our capital */
  devStrike: q.has('strike') ? num('strike', 0) : null,
  /** offline dev: mount the match chat panel offline (layout checks; nothing is sent) */
  devChat: q.get('chat') === '1',
  /** offline dev: our credits after the skip (`t`), e.g. to try subs and carriers at once */
  devCash: q.has('cash') ? num('cash', 0) : null,
  /** log per-stage render timings */
  prof: q.get('prof') === '1',
  /** render stages to skip (perf experiments), e.g. "territory,crt" */
  skipStages: new Set((q.get('skip') ?? '').split(',').filter(Boolean)),
};
